import { truncateLength } from "@/components/app-layout/consts";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { GroupedTable } from "@/components/GroupedTable/GroupedTable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOrganizationMembersData } from "@/data/use-organizations-data";
import { useGlobalUsersData } from "@/data/use-users-data";
import { GetUsersDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export function InviteOrganizationMemberDialog({
  organizationUuid,
}: {
  organizationUuid: string | null | undefined;
}) {
  const { users, isLoading: globalUsersLoading } = useGlobalUsersData();
  const {
    members,
    isLoading: membersLoading,
    inviteMember,
  } = useOrganizationMembersData(organizationUuid ?? null);

  const [search, setSearch] = useState("");

  const isLoading = !organizationUuid || globalUsersLoading || membersLoading;

  const availableUsers = useMemo(() => {
    return users.filter(
      (user) => !members.some((member) => member.uuid === user.uuid),
    );
  }, [users, members]);

  const filteredUsers = useMemo(() => {
    return availableUsers.filter((user) => {
      return (
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [availableUsers, search]);

  const handleInvite = async (user: GetUsersDto["data"][number]) => {
    try {
      await inviteMember(user.uuid);
      toast.success(`Invited ${user.email}.`);
    } catch {
      toast.error("Failed to invite member.");
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="default" disabled={isLoading}>
          <FontAwesomeIcon icon={faAdd} />
          Invite
        </Button>
      </DialogTrigger>
      <DialogContent className="flex w-fit max-w-4xl min-w-sm flex-col">
        <DialogHeader>
          <DialogTitle>Invite an existing user</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="flex-1 overflow-y-auto">
          <GroupedTable
            data={[
              {
                groupId: "users",
                label: "Users",
                items: filteredUsers,
              },
            ]}
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
            ]}
            renderCell={{
              name: (_, row) => <UserDisplayName user={row} />,
              email: (value) =>
                String(value).length > truncateLength ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{`${String(value).slice(0, truncateLength)}…`}</span>
                      </TooltipTrigger>
                      <TooltipContent>{String(value)}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  String(value)
                ),
            }}
            renderActions={({ row }) => (
              <Button size="sm" onClick={() => void handleInvite(row)}>
                <FontAwesomeIcon icon={faAdd} />
              </Button>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
